import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Page from "../../components/Page";
import {
  Table,
  Breadcrumb,
  Segment,
  Loader,
  Dimmer,
  Form,
  Button,
  Icon
} from "semantic-ui-react";
import _ from "lodash";
import { post } from '../../util/api';
import { Pie } from 'react-chartjs-2';

const Polls = () => {
  const { t } = useTranslation();
  const product = useSelector(state => state.product);
  const pid = product.personalize.id;
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [viewMode, setViewMode] = useState('table');

  const generateRandomColors = (count) => {
    const baseColors = [
      { h: 200, s: 75, l: 45 },
      { h: 145, s: 65, l: 40 },
      { h: 350, s: 70, l: 45 },
      { h: 280, s: 60, l: 45 },
      { h: 35, s: 85, l: 50 },
    ];
  
    const colors = [];
    
    for (let i = 0; i < count; i++) {
      const baseColor = baseColors[i % baseColors.length];
      const hueVariation = Math.random() * 20 - 10;
      const saturationVariation = Math.random() * 10 - 5;
      const lightnessVariation = Math.random() * 10 - 5;
      const hue = Math.round(baseColor.h + hueVariation);
      const saturation = Math.round(baseColor.s + saturationVariation);
      const lightness = Math.round(baseColor.l + lightnessVariation);
      const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      colors.push(color);
    }
  
    return {
      backgroundColor: colors,
      borderColor: colors.map(color => {
        return color.replace(/(\d+)%\)$/, (match, lightness) => 
          `${Math.max(parseInt(lightness) - 10, 0)}%)`
        );
      })
    };
  };

  const prepareChartData = () => {
    const optionCounts = _.countBy(filteredPolls, 'extra.options');
    const colors = generateRandomColors(Object.keys(optionCounts).length);
    const total = Object.values(optionCounts).reduce((a, b) => a + b, 0);
    
    return {
      labels: Object.keys(optionCounts).map(label => {
        const value = optionCounts[label];
        const percentage = Math.round((value / total) * 100);
        return `%${percentage} - ${label}`;
      }),
      datasets: [
        {
          data: Object.values(optionCounts),
          backgroundColor: colors.backgroundColor,
          borderColor: colors.borderColor,
          borderWidth: 1,
        },
      ],
    };
  };

  const fetchPolls = async () => {
    setLoading(true);
    try {
      const res = await post("/personalize/polls", { pid });
      if (res.data && res.data.success && Array.isArray(res.data.result)) {
        setPolls(res.data.result);
      } else {
        console.error("Error:", res);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (clickedColumn) => {
    if (sortColumn !== clickedColumn) {
      setSortColumn(clickedColumn);
      setSortDirection('ascending');
      setPolls(_.sortBy(polls, [clickedColumn]));
      return;
    }
  
    setPolls(polls.reverse());
    setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
  };

  const filteredPolls = polls.filter((poll) => {
    return (!dateRange.start || new Date(poll.created_at) >= dateRange.start) &&
           (!dateRange.end || new Date(poll.created_at) <= dateRange.end);
  });

  const renderContent = () => {
    if (viewMode === 'table') {
      return (
        <Table celled sortable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={sortColumn === "extra.email" ? sortDirection : null}
                onClick={() => handleSort("extra.email")}
              >
                {t("qaCustomerEmail")}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === "extra.options" ? sortDirection : null}
                onClick={() => handleSort("extra.options")}
              >
                {t("pollOptions")}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === "extra.message" ? sortDirection : null}
                onClick={() => handleSort("extra.message")}
              >
                {t("feedback")}
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === "created_at" ? sortDirection : null}
                onClick={() => handleSort("created_at")}
              >
                {t("createdDate")}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!loading && filteredPolls.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan="4">{t("noDataFound")}</Table.Cell>
              </Table.Row>
            ) : (
              filteredPolls.map((poll) => (
                <Table.Row key={poll.id}>
                  <Table.Cell>{poll.extra.email}</Table.Cell>
                  <Table.Cell>{poll.extra.options}</Table.Cell>
                  <Table.Cell>{poll.extra.message}</Table.Cell>
                  <Table.Cell>
                    {new Date(poll.created_at).toLocaleString()}
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      );
    } else {
      return (
        <div style={{ height: '400px', display: 'flex', justifyContent: 'center' }}>
          <Pie
            data={prepareChartData()}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'left',
                  labels: {
                    boxWidth: 20,
                    generateLabels: (chart) => {
                      const data = chart.data;
                      if (data.labels.length && data.datasets.length) {
                        return data.labels.map((label, index) => {
                          const dataset = data.datasets[0];
                          return {
                            text: label,
                            fillStyle: dataset.backgroundColor[index],
                            strokeStyle: dataset.borderColor[index],
                            lineWidth: dataset.borderWidth,
                            hidden: false,
                            index: index
                          };
                        });
                      }
                      return [];
                    }
                  }
                }
              }
            }}
          />
        </div>
      );
    }
  };

  useEffect(() => {
    fetchPolls();
  }, [pid]);

  return (
    <Page
      icon="chart line"
      title={t("polls")}
      header={
        <Breadcrumb size="big">
          <Breadcrumb.Section>{t("poll")}</Breadcrumb.Section>
        </Breadcrumb>
      }
    >
      <Page.Wrap>
        <Segment>
          <Dimmer active={loading} inverted>
            <Loader>{t("qaLoading")}</Loader>
          </Dimmer>
          
          <Form>
            <Form.Group>
              <Form.Field width={7}>
                <label>{t("qaStartDate")}</label>
                <Form.Input
                  type="date"
                  onChange={(e) =>
                    handleDateRangeChange(
                      new Date(e.target.value),
                      dateRange.end
                    )
                  }
                />
              </Form.Field>
              <Form.Field width={7}>
                <label>{t("qaEndDate")}</label>
                <Form.Input
                  type="date"
                  onChange={(e) =>
                    handleDateRangeChange(
                      dateRange.start,
                      new Date(e.target.value)
                    )
                  }
                />
              </Form.Field>
              <Form.Field width={2}>
                <label>{t("viewType")}</label>
                <Button.Group style={{marginTop:'1px'}}>
                  <Button 
                    icon 
                    active={viewMode === 'table'}
                    onClick={() => setViewMode('table')}
                  >
                    <Icon name='table' />
                  </Button>
                  <Button 
                    icon
                    active={viewMode === 'chart'}
                    onClick={() => setViewMode('chart')}
                  >
                    <Icon name='pie chart' />
                  </Button>
                </Button.Group>
              </Form.Field>
            </Form.Group>
          </Form>

          {renderContent()}
        </Segment>
      </Page.Wrap>
    </Page>
  );
};

export default Polls;